import { useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Pencil, Eye, EyeOff, Check, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PropertiesManager({ open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const { company } = useCompany();
  const isAdmin = (user?.role || "ADMIN").toUpperCase() === "ADMIN";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/properties/manage");
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setNewName(""); load(); }
  }, [open, company]);

  const toggleHide = async (name, hidden) => {
    try {
      await api.post("/properties/hide", { name, hidden });
      toast.success(hidden ? `Hidden "${name}"` : `Shown "${name}"`);
      load();
      onChanged?.();
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const saveRename = async () => {
    if (!editing) return;
    const oldName = editing.name;
    const newValue = editing.value.trim();
    if (!newValue || newValue === oldName) return setEditing(null);
    try {
      if (editing.id) {
        // Standalone property — rename via PATCH (doesn't touch bookings)
        await api.patch(`/properties/${editing.id}`, { name: newValue });
        toast.success(`Renamed to "${newValue}"`);
      } else {
        // Legacy: booking-derived property. Admin-only bulk rename touches booking records.
        if (!isAdmin) {
          toast.error("Only admin can rename properties already used by bookings");
          return setEditing(null);
        }
        if (!window.confirm(`Rename "${oldName}" to "${newValue}" across every booking? This modifies existing records.`)) {
          return setEditing(null);
        }
        const r = await api.post("/properties/rename", { old_name: oldName, new_name: newValue });
        toast.success(`Renamed (${r.data.updated} bookings updated)`);
      }
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed");
    }
  };

  const addProperty = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/properties/create", { name });
      toast.success(`Added "${name}" to ${company}`);
      setNewName("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add property");
    } finally { setCreating(false); }
  };

  const removeProperty = async (r) => {
    if (!r.id) {
      toast.error("Cannot delete — this property has bookings linked to it");
      return;
    }
    if (!window.confirm(`Delete property "${r.name}" from ${company}?`)) return;
    try {
      await api.delete(`/properties/${r.id}`);
      toast.success(`Deleted "${r.name}"`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="properties-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage Properties — {company}</DialogTitle>
          <DialogDescription>
            Add properties for this company. Names must be unique per company and are never shown to other companies.
            Booking records are never modified by add / edit / delete here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={addProperty} className="flex gap-2" data-testid="property-add-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New property name (e.g. 45 Farley St)"
            className="h-10 rounded-sm border-slate-300"
            data-testid="property-add-input"
          />
          <Button
            type="submit"
            disabled={creating || !newName.trim()}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white shrink-0"
            data-testid="property-add-submit"
          >
            <Plus className="w-4 h-4 mr-1" />
            {creating ? "Adding…" : "Add"}
          </Button>
        </form>

        <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-sm">
          {loading ? (
            <div className="p-6 text-sm text-slate-500 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No properties yet for {company}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Property</th>
                  <th className="text-right px-3 py-2 font-semibold">Bookings</th>
                  <th className="text-center px-3 py-2 font-semibold w-24">Visible</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name} className="border-b border-slate-100" data-testid={`prop-row-${r.name}`}>
                    <td className="px-3 py-2">
                      {editing?.name === r.name ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            className="h-8 rounded-sm text-sm"
                            autoFocus
                            data-testid={`prop-rename-input-${r.name}`}
                          />
                          <button onClick={saveRename} className="text-emerald-700 hover:text-emerald-900" data-testid={`prop-save-${r.name}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${r.hidden ? "text-slate-400 line-through" : "text-slate-900"}`}>
                            {r.name}
                          </span>
                          {r.id && (<span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">Owned</span>)}
                          <button
                            onClick={() => setEditing({ id: r.id, name: r.name, value: r.name })}
                            className="text-slate-400 hover:text-blue-900"
                            data-testid={`prop-edit-${r.name}`}
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.count}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-2">
                        {r.hidden ? <EyeOff className="w-3.5 h-3.5 text-slate-400" /> : <Eye className="w-3.5 h-3.5 text-emerald-700" />}
                        <Switch
                          checked={!r.hidden}
                          onCheckedChange={(v) => toggleHide(r.name, !v)}
                          data-testid={`prop-toggle-${r.name}`}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.id && r.count === 0 && (
                        <button
                          onClick={() => removeProperty(r)}
                          className="text-slate-400 hover:text-rose-700"
                          data-testid={`prop-delete-${r.name}`}
                          title="Delete standalone property"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white" data-testid="properties-manager-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
